import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { Eyebrow } from "@/components/kit";

const REVIEWS = [
  {
    name: "Sander Vermeulen",
    role: "Eigenaar",
    company: "Vermeulen Installatietechniek",
    service: "SEO + Webdesign AI",
    result: "3× meer offerteaanvragen",
    quote:
      "Binnen vijf maanden van pagina 4 naar de top-3 in Google. Er komen nu wekelijks offerteaanvragen binnen zonder dat we er iets voor hoeven doen. Tim zegt waar het op staat — en levert.",
  },
  {
    name: "Lisanne de Graaf",
    role: "Founder",
    company: "Studio Nova Interieur",
    service: "Social Media Advertising",
    result: "ROAS van 1,8 naar 6,2",
    quote:
      "Eindelijk een bureau dat niet over bereik praat, maar over omzet. Onze Meta-campagnes draaien nu op een ROAS van 6,2 — dat was 1,8 toen we begonnen. Elke euro is verantwoord.",
  },
  {
    name: "Mark Janssen",
    role: "Mede-eigenaar",
    company: "Janssen & Zonen Hoveniers",
    service: "Webdesign AI",
    result: "Agenda vol tot het najaar",
    quote:
      "De gratis AI Growth Scan was eerlijker dan adviezen waar ik eerder voor betaald heb. Twee weken later stond onze nieuwe site live — en de aanvragen stromen binnen.",
  },
  {
    name: "Femke van Dijk",
    role: "Marketingmanager",
    company: "Delta Kozijnen & Interieur",
    service: "SEO + Content",
    result: "+112% organische omzet",
    quote:
      "Geen accountmanagers, geen wollige rapporten. Eén appje naar Tim en het is geregeld. Onze organische omzet is in acht maanden verdubbeld.",
  },
  {
    name: "Jeroen Bakker",
    role: "Eigenaar",
    company: "Bakker Sportvoeding",
    service: "CRO + Webflow",
    result: "+186% conversie",
    quote:
      "Zelfde webshop, zelfde bezoekers, bijna drie keer zoveel bestellingen. De conversie-audit verdiende zichzelf in de eerste week al terug.",
  },
  {
    name: "Priya Sharma",
    role: "Loopbaancoach",
    company: "PS Coaching",
    service: "AI Optimalisatie (GEO)",
    result: "Genoemd in ChatGPT",
    quote:
      "Als ZZP'er dacht ik dat serieuze marketing niet voor mij was weggelegd. Nu word ik letterlijk genoemd als iemand ChatGPT om een loopbaancoach in mijn regio vraagt. Bizar goed.",
  },
];

const initials = (name) => {
  const parts = name.split(" ");
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Stars = ({ className = "" }) => (
  <div className={`flex gap-1 ${className}`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} className="w-4 h-4 fill-electric text-electric" />
    ))}
  </div>
);

const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const review = REVIEWS[index];

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + REVIEWS.length) % REVIEWS.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => go(1), 6500);
    return () => clearInterval(t);
  }, [paused, go, index]);

  return (
    <section
      id="reviews"
      data-testid="testimonials-section"
      className="relative bg-ivory py-24 lg:py-32 overflow-hidden"
    >
      <Quote className="pointer-events-none absolute -top-8 right-8 w-64 h-64 text-electric/[0.05] -scale-x-100" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left: header + aggregate */}
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <Eyebrow>Reviews · Wat klanten zeggen</Eyebrow>
            <h2 className="mt-6 font-display font-black text-coal tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
              Ondernemers die je <span className="text-gradient-blue">voorgingen.</span>
            </h2>

            <div className="mt-8 rounded-2xl border border-ivory-400 bg-white p-6 shadow-card-light inline-block">
              <div className="flex items-end gap-3">
                <span data-testid="reviews-aggregate-score" className="font-display font-black text-5xl text-coal leading-none">4.8</span>
                <div className="pb-1">
                  <Stars />
                  <div className="mt-1 text-xs text-coal/55">op basis van Google-reviews</div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3">
              <button
                onClick={() => go(-1)}
                data-testid="reviews-prev"
                aria-label="Vorige review"
                className="w-11 h-11 rounded-full border border-coal/15 text-coal grid place-items-center hover:bg-electric hover:border-electric hover:text-white transition-colors duration-300"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => go(1)}
                data-testid="reviews-next"
                aria-label="Volgende review"
                className="w-11 h-11 rounded-full border border-coal/15 text-coal grid place-items-center hover:bg-electric hover:border-electric hover:text-white transition-colors duration-300"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
              <span className="ml-2 font-mono text-xs text-coal/40 tracking-[0.2em]">
                {String(index + 1).padStart(2, "0")} / {String(REVIEWS.length).padStart(2, "0")}
              </span>
            </div>
          </div>

          {/* Right: sliding review card */}
          <div
            className="lg:col-span-8"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <div className="relative min-h-[380px] sm:min-h-[330px]">
              <AnimatePresence mode="wait">
                <motion.figure
                  key={index}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`review-card-${index}`}
                  className="rounded-[1.75rem] border border-ivory-400 bg-white p-8 lg:p-12 shadow-card-lift"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-7">
                    <Stars />
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-electric/10 border border-electric/20 text-electric-700 text-xs font-semibold px-3.5 py-1.5">
                      {review.result}
                    </span>
                  </div>

                  <blockquote className="font-display font-bold text-2xl lg:text-[2rem] text-coal leading-[1.25] tracking-tight text-balance">
                    &ldquo;{review.quote}&rdquo;
                  </blockquote>

                  <figcaption className="mt-9 pt-7 border-t border-ivory-300 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-electric text-white font-display font-bold grid place-items-center text-sm">
                        {initials(review.name)}
                      </div>
                      <div>
                        <div className="font-display font-bold text-coal leading-tight">{review.name}</div>
                        <div className="text-sm text-coal/55">{review.role} · {review.company}</div>
                      </div>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-coal/40">
                      {review.service}
                    </span>
                  </figcaption>
                </motion.figure>
              </AnimatePresence>
            </div>

            <div className="mt-6 flex items-center justify-center gap-2">
              {REVIEWS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  data-testid={`reviews-dot-${i}`}
                  aria-label={`Review ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index ? "w-8 bg-electric" : "w-2.5 bg-coal/15 hover:bg-coal/30"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
